import { useCallback, useMemo } from "react";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import i18n from "../../i18n";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import { useDataTable, type DataTableFilteredRequest } from "../../components/DataTable/TableHook";
import { useOptions } from "../../components/Hooks/useOptions";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { DeleteButton } from "../../components/Buttons/DeleteButton";
import { geologyAjax } from "../../ajax/GeologyAjax";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { GeoRehandleGroupDTO, GetGeoRehandleGroupsPagedResultDTO } from "../../DTOs/Geology/GeoRehandleGroupDTO";
import { MaterialTypeClassDTO } from "../../DTOs/MaterialTypeClassDTO";
import { TripleShift } from "../../types/Shift";
import { ExpandedRehandleGroupRow } from "./ExpandedRehandleGroupRow";
import RehandleGroupsTableFooter from "./RehandleGroupsTableFooter";

interface RehandleGroupsTableProps {
  selectedDate: DateTime;
  selectedShift: TripleShift;
  isCrushing: boolean;
  /** operationId to the machine's plant ref, from whichever picker the host screen already loads. */
  machineLabel: (operationId: number) => string;
  locationLabel: (locationId?: number) => string;
  bucketTypeLabel: (bucketTypeId?: number) => string;
  isShiftLocked: boolean;
  isOnline: boolean;
}

type GroupsTableMetaData = {
  totalLoads: number;
  totalTonnes: number;
};

/**
 * The Rehandle or Crushing screen collapsed to one row per digger, route, material and bucket type.
 *
 * Note: a separate component rather than a mode inside the two screens. Both already run past six hundred
 * lines around a single useDataTable whose fetch and translate functions are typed to the ungrouped row, and
 * making those switch shape at runtime would put the grouped and ungrouped paths in each other's way on a
 * screen people use every shift. Rendered instead of the main table, it shares nothing but the label
 * lookups the host has already loaded.
 *
 * Note: Material is a column here and nowhere else. It is part of the group key - tonnes are computed
 * through a material's density, so a group spanning two of them would sum figures that are not comparable -
 * and without the column two groups differing only by material would render as identical rows. Plant Model
 * is a column for the same reason: bucket type is in the key too.
 */
export const RehandleGroupsTable: React.FC<RehandleGroupsTableProps> = ({
  selectedDate,
  selectedShift,
  isCrushing,
  machineLabel,
  locationLabel,
  bucketTypeLabel,
  isShiftLocked,
  isOnline
}) => {
  const { t } = useTranslation("geology");
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { options: materialTypeClasses } = useOptions<MaterialTypeClassDTO, MaterialTypeClassDTO>(
    ['references', 'materialTypeClasses'],
    () => referencesAjax.getMaterialTypeClasses({} as DataTableFilteredRequest),
    x => x,
  );

  const materialLabel = useCallback((id?: number) => {
    if (id == null) return "-";
    return materialTypeClasses.find(m => m.id === id)?.class ?? "-";
  }, [materialTypeClasses]);

  const getRows = useCallback(
    (request: object) => geologyAjax.getGeoRehandleGroups(selectedDate, selectedShift, isCrushing, request),
    [selectedDate, selectedShift, isCrushing]);

  // Note: the host's ungrouped table stays mounted behind this one, so it is refreshed too, or toggling back
  // would show rows this table has just deleted or accepted.
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandleGroups'] });
    queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', 'rehandleGroupConstituents'] });
    queryClient.invalidateQueries({ queryKey: ['dataTable', 'geology', isCrushing ? 'crushing' : 'rehandles'] });
  }, [queryClient, isCrushing]);

  const acceptGroup = useCallback(async (entry: GeoRehandleGroupDTO) => {
    const response = await geologyAjax.verifyGeoRehandleGroup(
      selectedDate, selectedShift, isCrushing, entry.groupKey,
      { expectedCount: entry.constituentCount, maxGeoRehandleId: entry.maxGeoRehandleId });

    if (!response.success) {
      response.errors?.forEach((error, i) =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 4000 + i * 1000 }));
      invalidate();
      return;
    }

    // Note: reports the count, because accepting a group is one click standing for many rows and a person
    // has no other way to tell whether it did what they meant.
    const accepted = response.data ?? 0;
    enqueueSnackbar(t("groups.accepted", { count: accepted }),
      { variant: "success", autoHideDuration: 2500 });
    invalidate();
  }, [selectedDate, selectedShift, isCrushing, enqueueSnackbar, invalidate, t]);

  const deleteGroup = useCallback(async (entry: GeoRehandleGroupDTO) => {
    const response = await geologyAjax.deleteGeoRehandleGroup(
      selectedDate, selectedShift, isCrushing, entry.groupKey,
      { expectedCount: entry.constituentCount, maxGeoRehandleId: entry.maxGeoRehandleId });

    if (!response.success) {
      response.errors?.forEach((error, i) =>
        enqueueSnackbar(error.description, { variant: "error", autoHideDuration: 4000 + i * 1000 }));
      invalidate();
      return false;
    }

    const deleted = response.data ?? 0;
    enqueueSnackbar(t("groups.deleted", { count: deleted }),
      { variant: "success", autoHideDuration: 2500 });
    invalidate();
    return true;
  }, [selectedDate, selectedShift, isCrushing, enqueueSnackbar, invalidate, t]);

  const translateRows = useCallback((entry: GeoRehandleGroupDTO): TableRow<string> => {
    const allChecked = entry.checkedCount >= entry.constituentCount;

    return {
      id: entry.groupKey,
      values: [
        {
          name: isCrushing ? "Loader" : "Digger",
          label: isCrushing ? t("movement.loader") : t("movement.digger"),
          editable: false,
          type: "string",
          value: machineLabel(entry.operationId),
          tailwindClasses: "basis-[14%] py-1 pl-1",
        },
        {
          name: "Movement From",
          label: t("movement.movementFrom"),
          editable: false,
          type: "string",
          value: locationLabel(entry.movementFromId),
          tailwindClasses: "basis-[14%]",
        },
        {
          name: "Movement To",
          label: t("movement.movementTo"),
          editable: false,
          type: "string",
          value: locationLabel(entry.movementToId),
          tailwindClasses: "basis-[14%]",
        },
        {
          name: "Material",
          label: i18n.t("fields.material"),
          editable: false,
          type: "string",
          value: materialLabel(entry.materialTypeClassId),
          tailwindClasses: "basis-[12%]",
        },
        {
          name: "Plant Model",
          label: t("movement.plantModel"),
          editable: false,
          type: "string",
          value: bucketTypeLabel(entry.bucketTypeId),
          tailwindClasses: "basis-[12%]",
        },
        {
          name: "Loads",
          label: i18n.t("fields.loads"),
          editable: false,
          type: "integer",
          value: entry.loads,
          tailwindClasses: "basis-[8%] py-1",
        },
        {
          name: "Movements",
          label: t("groups.movements"),
          editable: false,
          type: "string",
          // Note: how many rows the loads came from, which is the number a person is really agreeing with.
          value: entry.constituentCount,
          tailwindClasses: "basis-[8%]",
        },
        {
          name: "Source",
          label: i18n.t("fields.source"),
          editable: false,
          type: "custom",
          value: entry.autoDetectedCount,
          tailwindClasses: "basis-[10%]",
          render: () => (
            <span className="text-theme-text text-sm">
              {entry.autoDetectedCount === 0
                ? t("groups.manual")
                : entry.manualCount === 0
                  ? t("groups.auto")
                  : t("groups.mixed", { auto: entry.autoDetectedCount, total: entry.constituentCount })}
            </span>
          )
        },
        {
          name: "Checked",
          label: t("groups.checked"),
          editable: false,
          type: "custom",
          value: entry.checkedCount,
          tailwindClasses: "basis-[8%]",
          render: () => (
            <VerificationIndicator
              isVerified={allChecked}
              checkedCount={entry.checkedCount}
              totalCount={entry.constituentCount}
              onAccept={allChecked ? undefined : () => acceptGroup(entry)}
              subject={t("groups.verifySubject")}
              disabled={isShiftLocked || !isOnline}
            />
          )
        },
        {
          name: "Actions",
          label: i18n.t("fields.actions"),
          editable: false,
          type: "custom",
          value: entry.groupKey,
          tailwindClasses: "basis-[10%] pr-1",
          render: () => (
            <DeleteButton
              confirmMessage={t("groups.deleteConfirm", { count: entry.constituentCount })}
              disabled={isShiftLocked || !isOnline}
              handleDelete={() => deleteGroup(entry)}
            />
          )
        }
      ]
    }
  }, [isCrushing, machineLabel, locationLabel, bucketTypeLabel, materialLabel, acceptGroup, deleteGroup, isShiftLocked, isOnline, t]);

  const selectMeta = useCallback((response: GetGeoRehandleGroupsPagedResultDTO) => ({
    totalLoads: response.totalLoads,
    totalTonnes: response.totalTonnes,
  }), []);

  const {
    rows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    pageSize,
    totalCount,
    totalPages,
    sortField,
    sortDirection,
    setPage,
    tableMetaData,
  } = useDataTable<GeoRehandleGroupDTO, GroupsTableMetaData, GetGeoRehandleGroupsPagedResultDTO>({
    queryKey: ['geology', 'rehandleGroups', isCrushing, selectedDate.toISODate(), selectedShift],
    fetchFn: getRows,
    initialPage: 1,
    pageSize: 25,
    selectMeta,
  });

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  const expandedRowRenderer = useCallback((row: TableRow<string>) => (
    <ExpandedRehandleGroupRow
      groupKey={row.id}
      selectedDate={selectedDate}
      selectedShift={selectedShift}
      isCrushing={isCrushing}
    />
  ), [selectedDate, selectedShift, isCrushing]);

  return (
    <DataTable<string>
      data={displayData}
      isLoading={isLoading}
      isBackgroundFetching={isBackgroundFetching}
      error={error}
      pageNumber={pageNumber}
      totalCount={totalCount}
      pageSize={pageSize}
      totalPages={totalPages}
      setPage={setPage}
      sortField={sortField}
      sortDirection={sortDirection}
      minTableWidthPixels={1080}
      fillHeight
      expandedRowRenderer={expandedRowRenderer}
      tableFooterContent={
        <RehandleGroupsTableFooter
          isLoading={isLoading}
          totalLoads={tableMetaData?.totalLoads}
          totalTonnes={tableMetaData?.totalTonnes}
        />
      }
    />
  );
}
